import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    CircularProgress,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { overrideHoliday } from '../dataProvider';

export interface OverrideHolidayDialogProps {
    open: boolean;
    /** The `YYYY-MM-DD` date being overridden, or `null` when the dialog is closed. */
    date: string | null;
    /** Human-readable holiday name, shown in the dialog title for context. */
    holidayName: string;
    onClose: () => void;
    /** Called after a successful override so the caller can refetch the list. */
    onSuccess: () => void;
}

/**
 * Dialog that lets an HR admin mark a public holiday date as not-a-holiday.
 *
 * Requires a non-blank reason before the "Mark as not a holiday" button is
 * enabled. Calls `POST /holidays/:date/override` via {@link overrideHoliday}.
 */
export const OverrideHolidayDialog: React.FC<OverrideHolidayDialogProps> = ({
    open,
    date,
    holidayName,
    onClose,
    onSuccess,
}) => {
    const notify = useNotify();
    const [reason, setReason] = useState<string>('');
    const [loading, setLoading] = useState(false);

    /** Closes the dialog and clears the reason field, ignoring the request while a submit is in flight. */
    const handleClose = () => {
        if (loading) return;
        setReason('');
        onClose();
    };

    /**
     * Submits the override, calling {@link overrideHoliday} with the trimmed
     * reason and notifying the user of the outcome before invoking `onSuccess`.
     */
    const handleSubmit = async () => {
        if (!date || !reason.trim()) return;

        setLoading(true);
        try {
            await overrideHoliday(date, reason.trim());
            notify('Holiday overridden successfully', { type: 'success' });
            setReason('');
            onSuccess();
        } catch (error: any) {
            const errorMessage = error?.body?.message || error?.message || 'Failed to override holiday';
            notify(errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const isValid = date !== null && reason.trim() !== '';

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Mark {holidayName || date} as Not a Holiday</DialogTitle>
            <DialogContent>
                <TextField
                    label="Reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    fullWidth
                    required
                    multiline
                    rows={2}
                    autoFocus
                    margin="normal"
                    disabled={loading}
                    inputProps={{ 'data-testid': 'override-reason-input' }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="warning"
                    disabled={!isValid || loading}
                    data-testid="override-submit-btn"
                >
                    {loading ? <CircularProgress size={20} /> : 'Mark as not a holiday'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
